import { useState, useEffect } from 'react';
import { useBillingStore } from '../../../store/billingStore';
import { Card, CardHeader, CardContent } from '../../../components/Card';
import { Button } from '../../../components/Button';
import { Badge } from '../../../components/Badge';
import { Plus, Search, RefreshCw, AlertTriangle, CreditCard } from 'lucide-react';
import { Input } from '../../../components/Input';
import toast from 'react-hot-toast';

export function Billing() {
    const { subscriptions, isLoading, fetchSubscriptions, createSubscription } = useBillingStore();
    const [search, setSearch] = useState('');

    useEffect(() => {
        fetchSubscriptions();
    }, []);

    const filteredSubs = subscriptions.filter(s => 
        s.planName.toLowerCase().includes(search.toLowerCase())
    );

    const formatCurrency = (val: number) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val);

    const handleCreateMock = async () => {
        try {
            await createSubscription({
                planName: 'Enterprise Plan',
                customerId: '00000000-0000-0000-0000-000000000000',
                amount: 499.00,
                billingCycle: 'monthly',
                status: 'active',
                nextBillingDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
            });
            toast.success('Subscription created');
        } catch (e) {
            toast.error('Failed to create subscription');
        }
    };

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="tracking-tight text-foreground font-display text-page-title">Billing & Subscriptions</h1>
                    <p className="text-muted-foreground">Manage recurring revenue and subscriptions.</p>
                </div>
                <Button variant="primary" onClick={handleCreateMock}>
                    <Plus className="w-4 h-4 mr-2" />
                    New Subscription
                </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card>
                    <CardContent className="p-4 flex items-center gap-4">
                        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center text-primary">
                            <RefreshCw className="w-5 h-5" />
                        </div>
                        <div>
                            <p className="text-sm font-medium text-muted-foreground">Active Subs</p>
                            <h3 className="text-foreground font-display text-metric">
                                {subscriptions.filter(s => s.status === 'active').length}
                            </h3>
                        </div>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="p-4 flex items-center gap-4">
                        <div className="w-10 h-10 rounded-lg bg-success/10 flex items-center justify-center text-success">
                            <CreditCard className="w-5 h-5" />
                        </div>
                        <div>
                            <p className="text-sm font-medium text-muted-foreground">MRR</p>
                            <h3 className="text-foreground font-display text-metric">
                                {formatCurrency(subscriptions.filter(s => s.status === 'active').reduce((acc, s) => acc + (s.billingCycle === 'monthly' ? s.amount : s.amount / 12), 0))}
                            </h3>
                        </div>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="p-4 flex items-center gap-4">
                        <div className="w-10 h-10 rounded-lg bg-destructive/10 flex items-center justify-center text-destructive">
                            <AlertTriangle className="w-5 h-5" />
                        </div>
                        <div>
                            <p className="text-sm font-medium text-muted-foreground">Past Due</p>
                            <h3 className="text-foreground font-display text-metric">
                                {subscriptions.filter(s => s.status === 'past_due').length}
                            </h3>
                        </div>
                    </CardContent>
                </Card>
            </div>

            <Card>
                <CardHeader>
                    <div className="flex justify-between items-center">
                        <h3 className="text-foreground font-display text-section-title">Subscriptions</h3>
                        <div className="relative w-64">
                            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                            <Input 
                                placeholder="Search plans..." 
                                className="pl-9"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />
                        </div>
                    </div>
                </CardHeader>
                <CardContent className="p-0">
                    {isLoading ? (
                        <div className="p-8 text-center text-muted-foreground">Loading...</div>
                    ) : (
                        <table className="w-full text-sm text-left">
                            <thead className="bg-secondary/50 text-muted-foreground font-semibold border-y border-border">
                                <tr>
                                    <th className="px-6 py-3">Plan Name</th>
                                    <th className="px-6 py-3">Amount</th>
                                    <th className="px-6 py-3">Cycle</th>
                                    <th className="px-6 py-3">Status</th>
                                    <th className="px-6 py-3">Next Billing</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-border">
                                {filteredSubs.length === 0 ? (
                                    <tr>
                                        <td colSpan={5} className="px-6 py-8 text-center text-muted-foreground">
                                            No subscriptions found.
                                        </td>
                                    </tr>
                                ) : filteredSubs.map(s => (
                                    <tr key={s.id} className="hover:bg-secondary/20 transition-colors">
                                        <td className="px-6 py-4 font-medium text-foreground">{s.planName}</td>
                                        <td className="px-6 py-4">{formatCurrency(s.amount)}</td>
                                        <td className="px-6 py-4 capitalize">{s.billingCycle}</td>
                                        <td className="px-6 py-4">
                                            <Badge variant={s.status === 'active' ? 'success' : s.status === 'past_due' ? 'destructive' : 'secondary'}>
                                                {s.status.toUpperCase()}
                                            </Badge>
                                        </td>
                                        <td className="px-6 py-4">
                                            {s.nextBillingDate ? new Date(s.nextBillingDate).toLocaleDateString() : '-'}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}
